import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Avatar from "@/design/components/Avatar";
import HeaderCard from "@/design/components/HeaderCard";
import FormGroup from "@/design/forms/FormGroup";
import { clearRecentPbxes, deletePbxFromRecents, togglePinStatePbxToRecents } from "@/store/reducers/settings";
import { saveSetting } from "@/utils/utility";
import Valkyrie, {
  viAngleDown,
  viBroom,
  viOrderAlphabeticalAsc,
  viOrderNumericalAsc,
  viStar,
  viThumbtack,
  viThumbtackSlash,
  viTrashCan,
} from "@sippy-platform/valkyrie";

import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from "@mui/material";

import SettingsOption from "./components/SettingsOption";

export default function Backstage() {
  const dispatch = useDispatch();

  const settings = useSelector((state) => state.auth.settings);
  const recentPbxes = useSelector((state) => state.settings.recentPbxes);

  const handleBackstageRecents = () => saveSetting("backstageRecents", settings.backstageRecents === 0 ? 1 : 0);

  // Refresh menu
  const [orderAnchorel, setOrderAnchorEl] = useState(null);
  const orderMenuOpen = Boolean(orderAnchorel);
  const openRefreshMenu = (event) => {
    setOrderAnchorEl(event.currentTarget);
  };
  const closeSortMenu = (sort) => {
    if (sort) {
      saveSetting("backstageSort", sort);
    }

    setOrderAnchorEl(null);
  };

  return (
    <Stack spacing={2}>
      <HeaderCard title="Backstage" sx={{ ".MuiCardHeader-title": { my: 0 } }} />
      <FormGroup title="Home">
        <Grid size={12}>
          <List>
            <SettingsOption onClick={handleBackstageRecents}>
              <ListItemIcon>
                <Valkyrie icon={viStar} />
              </ListItemIcon>
              <ListItemText primary="Show Recently used" />
              <Box sx={{ p: 0, justifyContent: "flex-end", pl: 1 }}>
                <Switch checked={!!settings.backstageRecents || false} sx={{ m: 0, mt: -0.5 }} />
              </Box>
            </SettingsOption>
            <SettingsOption onClick={openRefreshMenu}>
              <ListItemIcon>
                <Valkyrie icon={viOrderAlphabeticalAsc} />
              </ListItemIcon>
              <ListItemText primary="Sort PBXes by" />
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  gap: 0.5,
                  pl: 1,
                }}
              >
                <Typography variant="body2" sx={{ m: 0 }}>
                  {settings.backstageSort === "name" ? "Name" : "PBX number"}
                </Typography>
                <Valkyrie icon={viAngleDown} />
              </Stack>
            </SettingsOption>
            <Menu
              anchorEl={orderAnchorel}
              open={orderMenuOpen}
              onClose={() => closeSortMenu()}
              slotProps={{
                paper: {
                  elevation: 1,
                  sx: {
                    mt: 0.5,
                    border: "1px solid",
                    borderColor: "divider",
                    minWidth: 140,
                  },
                },
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => closeSortMenu("name")}
                selected={settings.backstageSort === "name"}
                sx={{ px: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: "32px !important" }}>
                  <Valkyrie icon={viOrderAlphabeticalAsc} />
                </ListItemIcon>
                Name
              </MenuItem>
              <MenuItem
                onClick={() => closeSortMenu("context")}
                selected={settings.backstageSort === "context"}
                sx={{ px: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: "32px !important" }}>
                  <Valkyrie icon={viOrderNumericalAsc} />
                </ListItemIcon>
                PBX number
              </MenuItem>
            </Menu>
          </List>
        </Grid>
      </FormGroup>
      <FormGroup
        title="Recently used"
        actions={
          <Button
            startIcon={<Valkyrie icon={viBroom} />}
            onClick={() => dispatch(clearRecentPbxes())}
            disabled={recentPbxes.length === 0}
          >
            Prune
          </Button>
        }
      >
        <Grid size={12}>
          <List>
            {recentPbxes.length >= 1 ? (
              recentPbxes.map((pbx) => (
                <ListItem
                  key={pbx.id}
                  sx={(theme) => ({
                    minHeight: 48,
                    borderRadius: 1,
                    pr: 12,
                    "&:nth-of-type(odd)": {
                      backgroundColor: "#f8f8f8",
                      ...theme.applyStyles("dark", {
                        backgroundColor: "#28282d",
                      }),
                    },
                    "&:hover": {
                      backgroundColor: "#efefef",
                      ...theme.applyStyles("dark", {
                        backgroundColor: "#323237",
                      }),
                    },
                  })}
                  secondaryAction={
                    <Stack
                      direction="row"
                      sx={{
                        gap: 0.5,
                      }}
                    >
                      <IconButton onClick={() => dispatch(togglePinStatePbxToRecents(pbx))}>
                        <Valkyrie icon={pbx.pinned ? viThumbtackSlash : viThumbtack} />
                      </IconButton>
                      <IconButton onClick={() => dispatch(deletePbxFromRecents(pbx))}>
                        <Valkyrie icon={viTrashCan} />
                      </IconButton>
                    </Stack>
                  }
                >
                  <ListItemAvatar sx={{ minWidth: 32 }}>
                    <Avatar text={pbx.name} px={24} />
                  </ListItemAvatar>
                  <ListItemText primary={pbx.name} />
                </ListItem>
              ))
            ) : (
              <ListItem
                sx={(theme) => ({
                  minHeight: 48,
                  borderRadius: 1,
                  backgroundColor: "#f8f8f8",
                  ...theme.applyStyles("dark", {
                    backgroundColor: "#28282d",
                  }),
                })}
              >
                <ListItemText primary="No recent PBXes" />
              </ListItem>
            )}
          </List>
        </Grid>
      </FormGroup>
    </Stack>
  );
}
